import React, { useState } from "react";

/**
 * SessionLimitModal — 동시접속 한도 초과 시 표시되는 모달.
 *
 * Props:
 *  - activeSessions: [{ sessionId, issuedAt, lastSeenAt, ipAddress, deviceLabel, userAgent, expiresAt }]
 *  - onCancel: () => void
 *  - onProceed: (revokeSessionIds: string[]) => Promise<void>
 *    선택한 세션 ID 들을 끊고 새 로그인을 시도한다.
 */
const SessionLimitModal = ({ activeSessions = [], onCancel, onProceed }) => {
  const [selected, setSelected] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const toggle = (sid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const formatTime = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(iso);
    }
  };

  const handleSubmit = async () => {
    if (selected.size === 0) {
      setError("최소 1개의 세션을 선택해 종료해주세요.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await onProceed(Array.from(selected));
    } catch (e) {
      setError(e?.message || "세션 종료 후 로그인에 실패했습니다.");
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2 style={styles.title}>동시 접속 한도 초과</h2>
        <p style={styles.subtitle}>
          이 계정으로 이미 {activeSessions.length}개의 세션이 활성화되어
          있습니다.
          <br />
          새로 로그인하려면 끊을 세션을 선택해주세요.
        </p>

        <div style={styles.list}>
          {activeSessions.map((s) => (
            <label key={s.sessionId} style={styles.item}>
              <input
                type="checkbox"
                checked={selected.has(s.sessionId)}
                onChange={() => toggle(s.sessionId)}
                disabled={submitting}
                style={styles.checkbox}
              />
              <div style={styles.itemInfo}>
                <div style={styles.itemTitle}>
                  {s.deviceLabel || "Unknown device"}
                </div>
                <div style={styles.itemMeta}>
                  <span>IP: {s.ipAddress || "-"}</span>
                  <span style={styles.metaSep}>·</span>
                  <span>마지막 활동: {formatTime(s.lastSeenAt)}</span>
                </div>
                <div style={styles.itemUA}>{s.userAgent || ""}</div>
              </div>
            </label>
          ))}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.actions}>
          <button
            onClick={onCancel}
            disabled={submitting}
            style={styles.cancelBtn}
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={styles.primaryBtn}
          >
            {submitting ? "처리 중..." : `${selected.size}개 끊고 로그인`}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    fontFamily: "'Malgun Gothic', '맑은 고딕', 'Pretendard', 'Noto Sans KR', -apple-system, sans-serif",
  },
  modal: {
    background: "#fff",
    borderRadius: "12px",
    padding: "24px",
    minWidth: "480px",
    maxWidth: "560px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
  },
  title: {
    fontSize: "20px",
    fontWeight: 700,
    marginBottom: "8px",
    color: "#1a1b1e",
  },
  subtitle: {
    fontSize: "14px",
    color: "#6b7280",
    marginBottom: "16px",
    lineHeight: 1.6,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    maxHeight: "320px",
    overflowY: "auto",
    marginBottom: "16px",
  },
  item: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    padding: "12px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    cursor: "pointer",
    background: "#f9fafb",
  },
  checkbox: {
    marginTop: "4px",
    width: "16px",
    height: "16px",
    cursor: "pointer",
  },
  itemInfo: { flex: 1, minWidth: 0 },
  itemTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#111827",
    marginBottom: "4px",
  },
  itemMeta: {
    fontSize: "12px",
    color: "#6b7280",
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
  },
  metaSep: { color: "#d1d5db" },
  itemUA: {
    fontSize: "11px",
    color: "#9ca3af",
    marginTop: "4px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  error: {
    padding: "10px 14px",
    borderRadius: "8px",
    background: "#fef2f2",
    color: "#dc2626",
    border: "1px solid #fecaca",
    fontSize: "13px",
    marginBottom: "12px",
  },
  actions: { display: "flex", justifyContent: "flex-end", gap: "8px" },
  cancelBtn: {
    padding: "10px 18px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    background: "#fff",
    color: "#374151",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
  },
  primaryBtn: {
    padding: "10px 18px",
    border: "none",
    borderRadius: "8px",
    background: "#18181b",
    color: "#fff",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },
};

export default SessionLimitModal;
